import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { SUPABASE_CONFIG } from '@/config/supabase-config';
import { useCurrentPageFeedbackSubview } from '@/lib/pageFeedback';
import { scrubPath } from '@/lib/uxSignals/uxSignalsCore';
import { installWebUxSignals } from '@/lib/uxSignals/uxSignalsWeb';

/**
 * PassiveUxSignals: no UI. Replaces the "Works / Needs work" pill (Doug LOCK
 * 2026-10-03) with passive signals: JS errors, slow loads, abandoned forms,
 * rage clicks and time-to-complete, keyed by the page path with ids replaced
 * by ':id' (the Brain resolver matches it to the page's node) plus the
 * page's subview (CRM tab, portal shuttle mode).
 *
 * Signed-in only: the adapter queues nothing without a Supabase session and
 * posts as that user through app-feedback (type 'batch' -> app_events).
 * The current page is also tagged on Sentry ('odv.screen') so crashes group
 * by page. See docs/architecture/PASSIVE_UX_SIGNALS.md.
 */
export default function PassiveUxSignals() {
  const location = useLocation();
  const subview = useCurrentPageFeedbackSubview(location.pathname);
  const signals = useRef(null);

  useEffect(() => {
    signals.current = installWebUxSignals({
      app: 'odv-truest',
      supabase,
      supabaseUrl: SUPABASE_CONFIG.functionsUrl || SUPABASE_CONFIG.url,
      anonKey: SUPABASE_CONFIG.anonKey,
      onScreen: (screen) => Sentry.setTag('odv.screen', screen),
    });
    return () => {
      signals.current?.destroy();
      signals.current = null;
    };
  }, []);

  useEffect(() => {
    signals.current?.setScreen(scrubPath(location.pathname), subview);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    signals.current?.setSubview(subview);
  }, [subview]);

  return null;
}
